// Getting a new company's seed onto the server (WEB_PLAN.md §5.2 step 3): "If the push fails part-way, the console
// shows 'Finishing setup…' and retries". A company without `settings` would send iPhones to onboarding, so:
// - the batch is built once (fixed ids and envelopes) and stored in localStorage before `create_company` is even
//   called, and what is still missing is stored after every answer, so a retry never duplicates anything and a
//   reload, a closed tab or a lost create answer can still be finished by the console;
// - each try sends only the items still missing; a few automatic tries with growing waits, then the person's Try again;
// - the run is not tied to the screen: if the console opens meanwhile (a membership check elsewhere), the push still
//   finishes, and `finishPendingCompanySetup` lets the console finish one left over from a closed tab.

import type { PushItem } from '@/contracts/wire';
import type { SessionCtx } from '@/session/types';
import { toEnvelopeDate } from '@/contracts/wireDate';
import { nowForEnvelope } from '@/sync/clock';
import { browserDeviceId } from '@/sync/replica';
import { pushSeedBatch } from './api';
import {
  decodePendingSetup,
  encodePendingSetup,
  isStaleOnWelcome,
  pendingSetupForCompany,
  seedRetryDelay,
  settleSeedPush,
  setupProblemFromError,
  setupProblemFromSettlement,
  type CompanyPendingSetup,
  type PendingSetup,
  type SeedEnvelope,
  type SetupProblem,
} from './companySetup';

const STORAGE_KEY = 'gt:welcome:pendingSetup';

/** The longest an offline try waits for the connection before it counts as a failed try. */
const OFFLINE_WAIT_MS = 30_000;

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function loadPendingSetup(authUserId: string): PendingSetup | null {
  try {
    return decodePendingSetup(storage()?.getItem(STORAGE_KEY), authUserId);
  } catch {
    return null;
  }
}

export function savePendingSetup(setup: PendingSetup): void {
  try {
    storage()?.setItem(STORAGE_KEY, encodePendingSetup(setup));
  } catch {
    // No storage (private mode, quota): the setup still runs, it just can't be resumed after a reload.
  }
}

/** Forgets the stored setup (all of it, or only when it belongs to `authUserId`). */
export function clearPendingSetup(authUserId?: string): void {
  try {
    const s = storage();
    if (!s) return;
    if (authUserId !== undefined && !decodePendingSetup(s.getItem(STORAGE_KEY), authUserId)) return;
    s.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}

/** /welcome: forgets this account's stored setup if it belongs to a company the account is no longer in. */
export function clearStalePendingSetup(authUserId: string): void {
  if (isStaleOnWelcome(loadPendingSetup(authUserId))) clearPendingSetup(authUserId);
}

/** The envelope every seed item gets: server-clock time with milliseconds and this browser's device id. */
export function seedEnvelope(): SeedEnvelope {
  return { modifiedAt: toEnvelopeDate(nowForEnvelope()), modifiedBy: browserDeviceId() };
}

export type SeedRunResult =
  | { done: true }
  | { done: false; remaining: PushItem[]; problem: SetupProblem };

export interface SeedRunOptions {
  /** A try failed and another one is coming: the screen switches to "Finishing setup…" with this text. */
  onRetrying?: (problem: SetupProblem) => void;
}

function isOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

/** Waits `ms`; when offline, until the browser is back online (at most OFFLINE_WAIT_MS). */
function wait(ms: number, forOnline: boolean): Promise<void> {
  return new Promise((resolve) => {
    if (!forOnline || typeof window === 'undefined' || isOnline()) {
      setTimeout(resolve, ms);
      return;
    }
    const done = () => {
      window.removeEventListener('online', done);
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(done, Math.max(ms, OFFLINE_WAIT_MS));
    window.addEventListener('online', done);
  });
}

/** One run per company at a time: the screen's Try again and a resume from elsewhere share it. */
const running = new Map<string, Promise<SeedRunResult>>();

/**
 * Pushes the setup's items until all are on the server, or until a problem needs the person (refused items, a session
 * problem, or the automatic tries ran out). The stored setup follows what is left, and is removed once it is all there.
 */
export function pushSeed(setup: CompanyPendingSetup, options: SeedRunOptions = {}): Promise<SeedRunResult> {
  const key = setup.companyId.toLowerCase();
  const existing = running.get(key);
  if (existing) return existing;
  const run = runSeed(setup, options).finally(() => running.delete(key));
  running.set(key, run);
  return run;
}

async function runSeed(setup: CompanyPendingSetup, options: SeedRunOptions): Promise<SeedRunResult> {
  let items = setup.items;
  for (let attempt = 0; ; attempt += 1) {
    let problem: SetupProblem;
    try {
      const result = await pushSeedBatch(setup.companyId, items);
      const settlement = settleSeedPush(items, result);
      items = settlement.remaining;
      if (items.length === 0) {
        clearPendingSetup(setup.authUserId);
        return { done: true };
      }
      savePendingSetup({ ...setup, items });
      problem = setupProblemFromSettlement(settlement);
    } catch (e) {
      problem = setupProblemFromError(e, isOnline());
    }
    const delay = problem.autoRetry ? seedRetryDelay(attempt) : null;
    if (delay === null) return { done: false, remaining: items, problem };
    options.onRetrying?.(problem);
    await wait(delay, problem.waitForOnline);
  }
}

/**
 * Finishes a company setup this browser started and couldn't complete (the tab was closed or reloaded before the seed
 * was all on the server, or `create_company`'s answer never arrived). For the console shell: call it with the session
 * once the company is open; 'none' when this browser has nothing unfinished for that company.
 */
export async function finishPendingCompanySetup(
  session: Pick<SessionCtx, 'authUserId' | 'companyId' | 'profileId'>,
): Promise<'none' | 'done' | 'failed'> {
  const setup = pendingSetupForCompany(loadPendingSetup(session.authUserId), session);
  if (!setup) return 'none';
  const result = await pushSeed(setup);
  return result.done ? 'done' : 'failed';
}
